import React, { useState } from 'react';
import s from './Wallet.module.sass';
import binance from '../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import etherium from '../../img/Ethereum-ETH-icon.png';
import bitcoin from '../../img/Bitcoin.svg.png';
import telegram from '../../img/telegram.webp';
import tron from '../../img/tron.png';
import usdclogo from '../../img/usdclogo.png';
import { useSimpleTranslation } from '../../hooks/useTranslation';
import FlagIcon from '../FlagIcon/FlagIcon';

const TransactionButton = ({ transaction, onActionChange, siteSettings }) => {
    const { t } = useSimpleTranslation(siteSettings);
    const isSuccess = transaction.status.toLowerCase() === 'success';
    const isInProcess = transaction.status.toLowerCase() === 'in process';
    const action = isSuccess || isInProcess ? t('wallet.actions.none', 'Нет') : (transaction.action === 'None' ? t('wallet.actions.none', 'Нет') : (transaction.action === 'Fix now' ? t('wallet.actions.fixNow', 'Исправить сейчас') : transaction.action));

    const handleClick = () => {
        if (isSuccess || isInProcess) return;

        const actions = [t('wallet.actions.fixNow', 'Исправить сейчас'), t('wallet.actions.none', 'Нет')];
        const currentActionIndex = actions.indexOf(action);
        const nextActionIndex = (currentActionIndex + 1) % actions.length;
        const newAction = actions[nextActionIndex];
        onActionChange(newAction);
    };

    return (
        <button
            className={`${s.actionBtn} ${action === 'None' ? s.actionNone : s.actionFix}`}
            onClick={handleClick}
            disabled={isSuccess || isInProcess}
        >
            {action}
        </button>
    );
};

const StatusButton = ({ status, onStatusChange, siteSettings }) => {
    const { t } = useSimpleTranslation(siteSettings);
    
    const handleClick = () => {
        const statuses = ['success', 'in process', 'error', 'pending', 'fixed'];
        const currentIndex = statuses.indexOf(status.toLowerCase());
        const nextIndex = (currentIndex + 1) % statuses.length;
        const newStatus = statuses[nextIndex];
        onStatusChange(newStatus);
    };

    const getStatusClass = (status) => {
        switch (status.toLowerCase()) {
            case 'fixed':
                return s.statusFixed;
            case 'pending':
                return s.statusPending;
            case 'success':
                return s.statusSuccess;
            case 'in process':
                return s.statusInProcess;
            case 'error':
                return s.statusError;
            default:
                return s.statusDefault;
        }
    };

    // Функция для перевода статусов
    const translateStatus = (status) => {
        switch (status.toLowerCase()) {
            case 'success':
                return t('wallet.statuses.success', 'Успешно');
            case 'pending':
                return t('wallet.statuses.pending', 'Ожидание');
            case 'in process':
                return t('wallet.statuses.inProcess', 'В процессе');
            case 'error':
                return t('wallet.statuses.error', 'Ошибка');
            case 'fixed':
                return t('wallet.statuses.fixed', 'Исправлено');
            default:
                return status;
        }
    };

    return (
        <button className={`${s.statusBtn} ${getStatusClass(status)}`} onClick={handleClick}>
            {translateStatus(status)}
        </button>
    );
};

export const Wallet = ({ maxTotalMoney, cryptoAdress, depositMoneyHistory, handleDelete, transactions, setTransactions, isNavbarCollapsed, selectedCrypto, bnbBalanca, getCryptoAmount, currentTariffLabel, currencyFormat, siteSettings, network, imgCoin, calculateCryptoAmount, customDepositMoney, customMaxMoney }) => {
    // Система переводов
    const { t } = useSimpleTranslation(siteSettings);
    
    const [selectedTimeFilter, setSelectedTimeFilter] = useState('24H');
    const [selectedTransactionFilter, setSelectedTransactionFilter] = useState(t('wallet.filters.allTransactions', 'Все транзакции'));

    // Функция для форматирования чисел согласно настройкам
    const formatNumber = (number, format = 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        
        const num = parseFloat(number);
        
        // Принудительное форматирование без зависимости от системной локали
        const parts = num.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        
        if (format === 'dot') {
            // Европейский формат: 10.294,98 (точка для тысяч, запятая для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return `${formattedInteger},${decimalPart}`;
        } else {
            // Американский формат: 10,294.98 (запятая для тысяч, точка для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return `${formattedInteger}.${decimalPart}`;
        }
    };

    // Функция для форматирования криптовалют с настраиваемым количеством знаков
    const formatCrypto = (number, decimalPlaces = 5) => {
        if (isNaN(number) || number === null || number === undefined) return '0.' + '0'.repeat(decimalPlaces);
        
        const num = parseFloat(number);
        return num.toFixed(decimalPlaces);
    };

    // Определяем статус активной транзакции withdrawal
    const getWithdrawalStatus = () => {
        if (!transactions || transactions.length === 0) return 'pending';
        
        // Ищем первую транзакцию типа withdrawal/Transfer
        const withdrawalTransaction = transactions.find(t => 
            t.type === `Transfer ${currencyFormat}` || t.type === 'Transfer RM' || t.type === 'Pengeluaran'
        );
        
        if (!withdrawalTransaction) return 'pending';
        
        return withdrawalTransaction.status.toLowerCase();
    };
    
    const withdrawalStatus = getWithdrawalStatus();
    
    // Определяем класс стилей в зависимости от статуса
    const getBalanceChangeClass = () => {
        switch (withdrawalStatus) {
            case 'error':
                return `${s.balanceChange} ${s.statusError}`;
            case 'pending':
                return `${s.balanceChange} ${s.statusPending}`;
            case 'in process':
                return `${s.balanceChange} ${s.statusInProcess}`;
            case 'success':
                return `${s.balanceChange} ${s.statusSuccess}`;
            default:
                return `${s.balanceChange} ${s.statusPending}`;
        }
    };
    
    // Определяем текст в зависимости от статуса
    const getWithdrawalText = () => {
        switch (withdrawalStatus) {
            case 'error':
                return t('wallet.withdrawalStatus.failed', 'вывод не удался - требуется действие');
            case 'pending':
                return t('wallet.withdrawalStatus.pending', 'в процессе вывода в данный момент');
            case 'in process':
                return t('wallet.withdrawalStatus.inProcess', 'вывод в процессе');
            case 'success':
                return t('wallet.withdrawalStatus.success', 'вывод успешно завершён');
            default:
                return t('wallet.withdrawalStatus.pending', 'в процессе вывода в данный момент');
        }
    };

    // Создаем массив assets на основе выбранной криптовалюты
    const cryptoAssetMap = {
        BTC: { name: 'Bitcoin', icon: bitcoin },
        ETH: { name: 'Ethereum', icon: etherium },
        BNB: { name: 'Binance Coin', icon: binance },
        USDC: { name: 'USD Coin', icon: usdclogo }
    };

    // Все доступные криптовалюты
    const allCryptoAssets = [
        { name: 'Bitcoin', symbol: 'BTC', icon: bitcoin, change: '+0.0%', changePositive: false },
        { name: 'Ethereum', symbol: 'ETH', icon: etherium, change: '+0.0%', changePositive: false },
        { name: 'Binance Coin', symbol: 'BNB', icon: binance, change: '+3147.2%', changePositive: true },
        { name: 'USD Coin', symbol: 'USDC', icon: usdclogo, change: '+0.1%', changePositive: true },
        { name: 'USDT', symbol: 'USDT', icon: tron, change: '+0.1%', changePositive: true }
    ];

    // Получаем другие криптовалюты (исключая выбранную)
    const otherCryptos = allCryptoAssets.filter(crypto => crypto.symbol !== selectedCrypto);
    
    // Проверяем, выбран ли тариф (если баланс больше нуля)
    const hasTariffSelected = maxTotalMoney > 0;
    
    // Рассчитываем криптовалюту на основе кастомной суммы или суммы из тарифа
    const getCryptoBalance = () => {
        // Если задана кастомная сумма выигрыша, используем её для расчета криптовалюты
        if (customMaxMoney && calculateCryptoAmount) {
            const customAmount = parseFloat(customMaxMoney);
            if (customAmount > 0) {
                const cryptoAmount = calculateCryptoAmount(customAmount, selectedCrypto);
                return formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5);
            }
        }
        // В режиме отзовика рассчитываем на основе сгенерированной суммы выигрыша
        if (siteSettings?.otzovikMode && calculateCryptoAmount && maxTotalMoney > 0) {
            const cryptoAmount = calculateCryptoAmount(maxTotalMoney, selectedCrypto);
            return formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5);
        } else if (hasTariffSelected && getCryptoAmount) {
            // В обычном режиме используем тарифы
            return getCryptoAmount(currentTariffLabel || '💰 1 ТАРИФ').split(' ')[0];
        } else {
            return formatCrypto(0, siteSettings?.cryptoDecimalPlaces || 5);
        }
    };
    
    // Рассчитываем процент роста на основе кастомной суммы депозита или суммы из тарифа
    const calculatePortfolioPercentage = () => {
        const currentBalance = parseFloat(maxTotalMoney.toString().replace(/,/g, ''));
        if (currentBalance === 0 || isNaN(currentBalance)) {
            return { value: '0.0%', positive: null };
        }
        
        // Если задана кастомная сумма депозита, используем её
        let initialInvestment;
        if (customDepositMoney) {
            const customDeposit = parseFloat(customDepositMoney);
            if (customDeposit > 0 && !isNaN(customDeposit)) {
                initialInvestment = customDeposit;
            }
        }
        
        // Если кастомная сумма не задана, используем сумму из тарифа
        if (!initialInvestment) {
            const tariffDepositAmounts = siteSettings?.tariffDepositAmounts || {
                '💰 1 ТАРИФ': 169,
                '❗❗ 2 ТАРИФ': 299,
                '❗❗❗ 3 ТАРИФ': 429,
                '❗❗❗❗ 4 ТАРИФ': 849,
                '⚠️ VIP ТАРИФ': 1899
            };
            initialInvestment = tariffDepositAmounts[currentTariffLabel || '💰 1 ТАРИФ'] || 169;
        }
        
        if (!initialInvestment || initialInvestment === 0) {
            return { value: '0.0%', positive: null };
        }
        
        const growthPercentage = ((currentBalance - initialInvestment) / initialInvestment * 100);
        const isPositive = growthPercentage >= 0;
        
        return {
            value: `${isPositive ? '+' : ''}${growthPercentage.toFixed(1)}%`,
            positive: isPositive
        };
    };
    
    const portfolioPercentage = calculatePortfolioPercentage();
    
    const portfolioAssets = [
        // Сверху - выбранная криптовалюта
        {
            name: cryptoAssetMap[selectedCrypto]?.name || 'Ethereum',
            symbol: selectedCrypto || 'ETH',
            icon: cryptoAssetMap[selectedCrypto]?.icon || etherium,
            balance: getCryptoBalance(),
                                        value: hasTariffSelected ? `${currencyFormat} ${formatNumber(maxTotalMoney, siteSettings?.numberFormat)}` : `${currencyFormat} 0.00`,
            change: hasTariffSelected ? portfolioPercentage.value : '0.0%',
            changePositive: hasTariffSelected ? portfolioPercentage.positive : null
        },
        // Снизу - остальные криптовалюты (исключая выбранную)
        ...otherCryptos.slice(0, 3).map(crypto => ({
            name: crypto.name,
            symbol: crypto.symbol,
            icon: crypto.icon,
            balance: formatCrypto(0, siteSettings?.cryptoDecimalPlaces || 5),
                            value: `${currencyFormat} 0.00`,
            change: '0.0%',
            changePositive: null
        }))
    ];

    const transactionFilters = [
        t('wallet.filters.allTransactions', 'Все транзакции'),
        t('wallet.filters.withdrawals', 'Выводы'),
        t('wallet.filters.deposits', 'Депозиты'),
        t('wallet.filters.conversions', 'Конвертации')
    ];
    const timeFilters = ['1H', '24H', '1W', '1M', '1Y'];

    // Функция для перевода типов транзакций
    const translateTransactionType = (type) => {
        // Торговая сессия - проверяем сначала, до проверки на startsWith
        if (type === 'Sesi Dagangan' || type?.trim() === 'Sesi Dagangan') {
            return t('wallet.transactionTypes.tradingSession', 'Торговая сессия');
        }
        
        // Transfer с любой валютой
        if (type.startsWith('Transfer ')) {
            const currency = type.replace('Transfer ', '');
            return `${t('wallet.transactionTypes.transfer', 'Перевод')} ${currency}`;
        }
        
        // Конвертация с любой криптовалютой  
        if (type.startsWith('Penukaran ')) {
            const crypto = type.replace('Penukaran ', '');
            return `${t('wallet.transactionTypes.conversion', 'Конвертация')} ${crypto}`;
        }
        
        // Депозит с любой валютой
        if (type.startsWith('Deposit ')) {
            const currency = type.replace('Deposit ', '');
            return `${t('wallet.transactionTypes.deposit', 'Депозит')} ${currency}`;
        }
        
        // Для неизвестных типов возвращаем как есть
        return type;
    };

    return (
        <div className={`${s.wallet} ${isNavbarCollapsed ? s.collapsed : ''}`}>


            {/* Balance & Portfolio Section */}
            <div className={s.topSection}>
                {/* Balance Card */}
                <div className={s.balanceSection}>
                    <div className={s.balanceCard}>
                        <div className={s.balanceHeader}>
                            <h2 className={s.sectionTitle}>{t('wallet.sections.totalBalance', 'Общий баланс')}</h2>
                            <div className={s.currencyFlag}>
                                <FlagIcon siteSettings={siteSettings} className={s.currencyFlagIcon} alt="Currency region" />
                            </div>
                        </div>
                        <div className={s.balanceAmount}>
                            <span className={s.amount}>{currencyFormat} 0.00</span>
                        </div>
                        <div className={getBalanceChangeClass()}>
                            {currencyFormat} {formatNumber(maxTotalMoney, siteSettings?.numberFormat)} {getWithdrawalText()}
                        </div>
                        <div className={s.balanceActions}>
                            <button className={s.depositBtn}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 5v14m-7-7l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {t('wallet.buttons.deposit', 'Депозит')}
                            </button>
                            <button className={s.withdrawBtn}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 19V5m-7 7l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {t('wallet.buttons.withdraw', 'Вывести')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Portfolio Section */}
                <div className={s.portfolioSection}>
                    <div className={s.portfolioHeader}>
                        <h2 className={s.sectionTitle}>{t('wallet.sections.yourPortfolio', 'Ваш портфель')}</h2>
                        <button className={s.viewAllBtn}>
                            {t('wallet.buttons.viewAll', 'Посмотреть все')}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className={s.portfolioGrid}>
                        {portfolioAssets.map((asset, index) => (
                            <div key={index} className={s.assetCard}>
                                <div className={s.assetHeader}>
                                    <img src={asset.icon} alt={asset.symbol} className={s.assetIcon} />
                                    <div className={s.assetInfo}>
                                        <h3 className={s.assetName}>{asset.name}</h3>
                                        <span className={s.assetSymbol}>{asset.symbol}</span>
                                    </div>
                                </div>
                                <div className={s.assetBalance}>
                                    <div className={s.assetAmount}>
                                        {asset.balance} <span className={s.assetUnit}>{asset.symbol}</span>
                                    </div>
                                    <div className={s.assetValue}>{asset.value}</div>
                                </div>
                                <div className={s.assetChange}>
                                    <span className={`${s.changeValue} ${asset.changePositive ? s.positive : s.neutral}`}>
                                        {asset.change}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className={s.changeIcon}>
                                            <path d={asset.changePositive ? "M7 14l5-5 5 5" : "M7 10l5 5 5-5"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Transaction History Section */}
            <div className={s.transactionSection}>
                <div className={s.transactionHeader}>
                    <h2 className={s.sectionTitle}>{t('wallet.sections.transactionHistory', 'История транзакций')}</h2>
                    
                    <div className={s.transactionFilters}>
                        <div className={s.filterGroup}>
                            {transactionFilters.map((filter) => (
                                <button
                                    key={filter}
                                    className={`${s.filterBtn} ${selectedTransactionFilter === filter ? s.active : ''}`}
                                    onClick={() => setSelectedTransactionFilter(filter)}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                        
                        <div className={s.timeFilterGroup}>
                            {timeFilters.map((filter) => (
                                <button
                                    key={filter}
                                    className={`${s.timeFilterBtn} ${selectedTimeFilter === filter ? s.active : ''}`}
                                    onClick={() => setSelectedTimeFilter(filter)}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={s.transactionTable}>
                    <div className={s.tableHeader}>
                        <div className={s.tableHeaderCell}>{t('wallet.tableHeaders.description', 'Описание')}</div>
                        <div className={s.tableHeaderCell}>{t('wallet.tableHeaders.currency', 'Валюта')}</div>
                        <div className={s.tableHeaderCell}>{t('wallet.tableHeaders.amount', 'Сумма')}</div>
                        <div className={s.tableHeaderCell}>{t('wallet.tableHeaders.status', 'Статус')}</div>
                        <div className={s.tableHeaderCell}>{t('wallet.tableHeaders.action', 'Действие')}</div>
                    </div>
                    
                    <div className={s.tableBody}>
                        {transactions.map((transaction) => (
                            <div key={transaction.id} className={s.tableRow}>
                                <div className={s.tableCell}>
                                    <div className={s.transactionInfo}>
                                        <div className={s.transactionIcon}>
                                            <img src={transaction.image} alt="Transaction" />
                                            {(transaction.status.toLowerCase() === 'error' || 
                                              transaction.status.toLowerCase() === 'in process' || 
                                              transaction.status.toLowerCase() === 'pending') && (
                                                <div className={`${s.statusIndicator} ${s[transaction.status.toLowerCase().replace(' ', '')]}`}>
                                                    !
                                                </div>
                                            )}
                                            {/* Иконка банка в правом нижнем углу - только для транзакций вывода */}
                                            {imgCoin && imgCoin.img && transaction.type && (
                                                transaction.type.includes('Transfer') || 
                                                transaction.type.includes('transfer') ||
                                                transaction.type.includes('Перевод') ||
                                                transaction.type.includes('перевод')
                                            ) && (
                                                <div className={s.bankIconOverlay}>
                                                    <img src={imgCoin.img} alt={network} />
                                                </div>
                                            )}
                                        </div>
                                        <div className={s.transactionDetails}>
                                            <div className={s.transactionType}>{translateTransactionType(transaction.type)}</div>
                                            <div className={s.transactionDescription}>
                                                {(transaction.type === 'Transfer RM' || transaction.type === `Transfer ${currencyFormat}`)
                                                    ? `${siteSettings?.bankAccountDisplayName || 'IBAN/BIC'}: ${cryptoAdress}` 
                                                    : transaction.description
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={s.tableCell}>
                                    <span className={s.currency}>
                                        {transaction.type === `Penukaran ${selectedCrypto}` ? `${selectedCrypto} → ${currencyFormat}` : currencyFormat}
                                    </span>
                                </div>
                                
                                <div className={s.tableCell}>
                                    <div className={s.amountInfo}>
                                        <div className={s.transactionAmount}>
                                            {transaction.amount.includes(currencyFormat) 
                                                ? transaction.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                                : transaction.amount
                                            }
                                        </div>
                                        <div className={s.transactionValue}>
                                            {transaction.type.includes('Deposit') ? '+' : '-'}
                                            {/* Для Transfer показываем сумму вывода из maxTotalMoney */}
                                            {transaction.type === 'Transfer RM' || transaction.type === `Transfer ${currencyFormat}` 
                                                ? `${currencyFormat} ${formatNumber(maxTotalMoney, siteSettings?.numberFormat)}`
                                                : (transaction.amount.includes(currencyFormat) 
                                                    ? transaction.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                                    : transaction.amount)
                                            }
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={s.tableCell}>
                                    <StatusButton
                                        status={transaction.status}
                                        onStatusChange={(newStatus) => {
                                            setTransactions(prevTransactions =>
                                                prevTransactions.map(trans => {
                                                    if (trans.id === transaction.id) {
                                                        const updatedTransaction = { ...trans, status: newStatus };
                                                        if (newStatus.toLowerCase() === 'success' || newStatus.toLowerCase() === 'fixed') {
                                                            updatedTransaction.action = t('wallet.actions.none', 'Нет');
                                                        } else {
                                            updatedTransaction.action = t('wallet.actions.fixNow', 'Исправить сейчас');
                                        }
                                                        return updatedTransaction;
                                                    }
                                                    return trans;
                                                })
                                            );
                                        }}
                                        siteSettings={siteSettings}
                                    />
                                </div>
                                
                                <div className={s.tableCell}>
                                    <TransactionButton
                                        transaction={transaction}
                                        onActionChange={(newAction) => {
                                            setTransactions(prevTransactions =>
                                                prevTransactions.map(trans =>
                                                    trans.id === transaction.id ? { ...trans, action: newAction } : trans
                                                )
                                            );
                                        }}
                                        siteSettings={siteSettings}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pagination */}
                <div className={s.pagination}>
                    <span className={s.paginationInfo}>
                        {transactions.length} {t('wallet.pagination.transactions', 'Транзакций')}
                    </span>
                    <div className={s.pageControls}>
                        <button className={s.pageBtn}>1</button>
                    </div>
                    <div className={s.filterControls}>
                        <select className={s.pageSelect}>
                            <option>{t('wallet.pagination.all', 'Все')}</option>
                            <option>{t('wallet.buttons.deposit', 'Депозит')}</option>
                            <option>{t('wallet.filters.conversions', 'Конвертации')}</option>
                            <option>{t('wallet.pagination.transfer', 'Перевод')}</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};
